/**
 * Ask for a password reset link.
 *
 * This screen did not exist. `Login.tsx` has linked to `/auth/reset` for as long as it has had a
 * "Forgotten your password?" line, and nothing was ever registered at that path — so the link left
 * the SPA at a route React Router has no match for and rendered its default error page. Pages
 * rewrites `/auth/*` to the app (`public/_redirects`), so it was not even a 404 anyone could see in
 * a log: the request answered 200 and the app failed on arrival. Password reset is an MVP item
 * (implementation-plan.md line 498), which makes the link a promise the product had not kept.
 *
 * ## The answer is the same whether or not the address has an account
 *
 * security.md §11 names "user enumeration via signup/reset responses" as the threat and "uniform
 * responses" as the control, and GoTrue is built for it: `POST /recover` answers 200 for an address
 * with no account exactly as it does for one with. So this screen must never say "we sent you an
 * email" — that asserts the account exists. It says "if that address has an account", which is the
 * only sentence true in both cases.
 *
 * Distinguishing a FAILURE from that is still safe, because the failures are not about the address:
 * a spent email allowance and a request-rate ceiling are facts about the cell and the caller's IP.
 * Swallowing them into the neutral message would tell somebody a link is coming when none is.
 *
 * ## "Reset is not sending emails" is usually this screen keeping that promise
 *
 * Measured on the launch cell 2026-09-22, after exactly that report: the `/recover` POST reached
 * GoTrue and answered `200`, SES SMTP was sending (signup confirmations confirm within a minute of
 * every signup on that cell), and the address in the screenshot simply had no row in `auth.users`.
 * Nothing was broken. The uniform response is doing its job, and from the outside a working flow
 * and a dead one look identical — which is the cost of the control, not a defect in it.
 *
 * So the diagnostic order for the next report is: is there an account for that exact address, then
 * is there a `/recover` line in the auth logs, then is SMTP live. Only the last is a mail fault,
 * and a `200` on the second rules out the first two being at fault together. What this screen owes
 * the person in front of it is the way out below — the one thing it can offer without leaking.
 *
 * Turnstile belongs here — security.md §12 placement 3 — and is not built. It is not claimed
 * either: there is no bot-check line on this page, because an untrue security reassurance is worse
 * than none (the sentence Login.tsx had to have removed).
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Callout, Card, Field } from '@flowza/ui'
import { configStatus, getClient } from '../../supabase'
import { property } from '../../auth/password-failure'
import { AuthLayout } from './AuthLayout'

/**
 * Where the emailed link lands. Absolute, because GoTrue redirects to it from its own origin.
 *
 * It must be in the project's **Redirect URLs** allow-list, which the go-live runbook already sets
 * to `https://sign.<domain>/**` (§ Auth URL configuration). An unlisted `redirect_to` is not an
 * error a caller sees — GoTrue silently falls back to the Site URL, and the recovery tokens land on
 * the dashboard instead of this flow, where nothing reads them.
 */
export const RESET_CONFIRM_PATH = '/auth/reset/confirm'

type Outcome =
  | { readonly kind: 'idle' }
  | { readonly kind: 'busy' }
  /** Sent, or the address has no account. This screen cannot tell, and must not appear to. */
  | { readonly kind: 'requested'; readonly email: string }
  | { readonly kind: 'failed'; readonly title: string; readonly message: string }

/** Failures that are about the cell or the caller's IP — never about whether the address exists. */
export function classifyResetFailure(error: unknown): {
  readonly title: string
  readonly message: string
} {
  const code = property(error, 'code')
  if (code === 'over_email_send_rate_limit') {
    return {
      title: 'This cell cannot send another email just yet',
      message:
        'Nothing is wrong with that address. The email allowance is spent for the moment — wait a few minutes and ask again.',
    }
  }
  if (code === 'over_request_rate_limit') {
    return {
      title: 'Too many attempts',
      message: 'Wait a minute, then ask again.',
    }
  }
  return {
    title: 'We could not send that link',
    message: 'Something on our side refused the request. Please try again in a moment.',
  }
}

export default function Reset() {
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'idle' })
  const config = configStatus()

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const client = getClient()
    if (client === null) return
    const email = String(new FormData(event.currentTarget).get('email') ?? '').trim()
    setOutcome({ kind: 'busy' })
    const { error } = await client.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}${RESET_CONFIRM_PATH}`,
    })
    if (error) {
      setOutcome({ kind: 'failed', ...classifyResetFailure(error) })
      return
    }
    setOutcome({ kind: 'requested', email })
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We will email you a link that lets you set a new one."
      footer={
        <>
          Remembered it?{' '}
          <Link to="/auth/login" className="text-accent font-medium">
            Sign in
          </Link>
        </>
      }
    >
      {!config.ok ? (
        <div className="mb-4">
          <Callout tone="danger" title="This deployment is not configured">
            Missing build variables: {config.missing.join(', ')}. Sending a reset link needs them.
          </Callout>
        </div>
      ) : null}
      {outcome.kind === 'failed' ? (
        <div className="mb-4">
          <Callout tone="danger" title={outcome.title}>
            {outcome.message}
          </Callout>
        </div>
      ) : null}

      {outcome.kind === 'requested' ? (
        <Card>
          {/*
            "If" is the whole sentence. Saying "we sent a link to <address>" would answer "does this
            address have an account" to anyone who typed one, which is the oracle security.md §11
            names — and it is the reason GoTrue answers 200 for an unknown address in the first
            place.
          */}
          <Callout tone="info" title="Check your email">
            If {outcome.email} has an account, a link to set a new password is on its way. It
            expires, so use it soon. Nothing has changed about your password yet.
          </Callout>
          {/*
            The exhausted state needs a way out, and until this line existed there was none. An
            address with no account is answered by this very screen, so "no email arrived" has two
            ordinary causes and the screen named only one: the spam folder. The other is that the
            address was never registered — and somebody in that state could do nothing here but
            retype addresses, which is how a working reset flow gets reported as a broken one.

            Naming both causes leaks nothing, because this paragraph is drawn for EVERY submission.
            It is a statement about how the screen works, not about the address that was typed. The
            uniform-response control (security.md §11) is a property of what DIFFERS between a known
            and an unknown address, and nothing on this screen does.
          */}
          <p className="text-text-muted mb-0 mt-4 text-sm">
            No email after a few minutes? Check the spam folder. An address that was never
            registered gets no mail either — this screen answers the same way for both — so{' '}
            <button
              type="button"
              className="text-accent font-medium underline"
              onClick={() => setOutcome({ kind: 'idle' })}
            >
              try a different address
            </button>{' '}
            or{' '}
            <Link to="/auth/signup" className="text-accent font-medium underline">
              create an account
            </Link>
            .
          </p>
        </Card>
      ) : (
        <Card>
          <form className="flex flex-col gap-4" onSubmit={(e) => void onSubmit(e)}>
            <Field
              label="Email"
              type="email"
              name="email"
              autoComplete="username"
              required
              hint="The address you signed up with."
            />
            <Button
              type="submit"
              disabled={outcome.kind === 'busy' || !config.ok}
              className="mt-1 w-full"
            >
              {outcome.kind === 'busy' ? 'Sending…' : 'Email me a reset link'}
            </Button>
          </form>
        </Card>
      )}
    </AuthLayout>
  )
}
